import { Component } from '@theme/component';

/** Row wrapper that pairs an accessory checkbox with its optional variant select. */
const ROW = '.angebot-formular__zubehoer-zeile';
/** Class marking a generated error message, so they can all be cleared again. */
const ERROR = 'angebot-formular__feldfehler';

/**
 * Enhancements for the Bestpreis-Angebot request form.
 *
 * Three jobs. The first two are pure enhancement — the form is complete and submittable without
 * them. The third replaces browser validation with something the customer can actually see.
 *
 * 1. Pre-tick the accessories named in `?zubehoer=`, which blocks/angebot-button.liquid put there
 *    from the Candy Bar selection on the product page. Liquid cannot read query parameters, so the
 *    accessory list is rendered in full server-side and only the ticks happen here. A missing or
 *    stale parameter simply leaves everything unticked.
 *
 * 2. Disable the "Ausführung" select of an unticked accessory, because a disabled field is not
 *    submitted. Without JavaScript the select stays enabled and the e-mail carries one extra
 *    "Ausführung" line for an accessory nobody asked for — noise, not breakage.
 *
 * 3. Validate on submit and show the reason in the form.
 *    Horizon draws checkboxes as an SVG and hides the real input (`opacity: 0`, absolutely
 *    positioned — snippets/checkbox-styles.liquid). A browser asked to report a problem on such an
 *    input has nothing visible to anchor its bubble to, so the required consent box could refuse
 *    submission while telling the customer nothing. We therefore switch the native reporting off
 *    (only once this script runs, so the no-JS path keeps it) and write the messages into the page.
 *
 * @extends Component
 */
class AngebotFormular extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#applyUrlSelection();
    this.#syncSelects();
    this.#takeOverSubmitButton();

    // Capture on the host, not on the form: this survives the form being re-rendered and runs
    // before anything else could act on the event. Everything is looked up at event time rather
    // than cached, so there is no state that can go stale and silently let a submission through.
    this.addEventListener('submit', this.#onSubmit, true);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('input', this.#onInput);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#onSubmit, true);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('input', this.#onInput);

    super.disconnectedCallback();
  }

  /** @returns {HTMLFormElement | null} */
  get #form() {
    return this.querySelector('form');
  }

  /** @returns {HTMLButtonElement | null} */
  get #submitButton() {
    const button = this.querySelector('[data-angebot-submit]');
    return button instanceof HTMLButtonElement ? button : null;
  }

  /**
   * Closes the native submit path once JavaScript is in charge.
   *
   * The button ships as `type="submit"` so the form still works without JavaScript, with the
   * browser doing the validating. As soon as this runs it becomes `type="button"`: from then on
   * nothing but our own code can submit the form, so an incomplete form cannot reach Shopify —
   * which would bounce it to the home page with nothing said.
   */
  #takeOverSubmitButton() {
    const form = this.#form;
    const button = this.#submitButton;
    if (!form || !button) return;

    form.noValidate = true;
    button.type = 'button';
    button.addEventListener('click', this.#onSubmitClick);

    this.#reflectValidity();
  }

  /** @param {Event} event */
  #onSubmitClick = (event) => {
    event.preventDefault();

    const form = this.#form;
    if (!form) return;

    if (this.#reportProblems(form)) return;

    // Validated above; `requestSubmit` would re-run the checks we just did.
    form.submit();
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const { target } = event;
    if (target instanceof HTMLInputElement && target.dataset.zubehoerHandle) this.#syncSelect(target);
    if (target instanceof HTMLInputElement && target.checked) this.#clearFieldError(target);
    this.#reflectValidity();
  };

  /** @param {Event} event */
  #onInput = (event) => {
    const { target } = event;
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
      if (target.checkValidity()) this.#clearFieldError(target);
      this.#reflectValidity();
    }
  };

  /**
   * Backstop for any submit that reaches the form another way — the Enter key in a text field,
   * or a browser that submits despite the button no longer being of type submit.
   * @param {SubmitEvent} event
   */
  #onSubmit = (event) => {
    const form = this.#form;
    if (!form) return;

    if (this.#reportProblems(form)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  /**
   * Marks every invalid field and moves to the first one.
   * @param {HTMLFormElement} form
   * @returns {boolean} true when something is wrong and the form must not be sent
   */
  #reportProblems(form) {
    this.#clearErrors();

    const invalid = /** @type {(HTMLInputElement | HTMLTextAreaElement)[]} */ (
      [...form.elements].filter(
        (element) =>
          (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) &&
          element.willValidate &&
          !element.checkValidity()
      )
    );

    this.#reflectValidity(invalid.length === 0);

    const first = invalid[0];
    if (!first) return false;

    for (const field of invalid) this.#markInvalid(field);

    // A hidden checkbox cannot be scrolled to usefully; its visible row can.
    const anchor = first.closest('.checkbox, .angebot-formular__field') ?? first;
    anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
    first.focus({ preventScroll: true });

    return true;
  }

  /**
   * Greys the submit button out while the form is incomplete. `aria-disabled` rather than
   * `disabled`, so the button stays clickable and a click can say what is missing — a truly
   * disabled button would leave the customer guessing.
   * @param {boolean} [known]
   */
  #reflectValidity(known) {
    const button = this.#submitButton;
    const form = this.#form;
    if (!button || !form) return;

    const complete = known ?? form.checkValidity();
    button.setAttribute('aria-disabled', complete ? 'false' : 'true');
  }

  /**
   * German message for the one failure we care about, rather than the browser's own wording,
   * which varies by browser and by UI language rather than by shop language.
   * @param {HTMLInputElement | HTMLTextAreaElement} field
   * @returns {string}
   */
  #message(field) {
    if (field instanceof HTMLInputElement && field.type === 'checkbox') {
      return 'Bitte bestätigen Sie die Datenschutzerklärung, damit wir Ihre Anfrage bearbeiten dürfen.';
    }
    if (field.validity.typeMismatch && field instanceof HTMLInputElement && field.type === 'email') {
      return 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
    }
    if (field.validity.valueMissing) return 'Bitte füllen Sie dieses Feld aus.';
    return field.validationMessage;
  }

  /** @param {HTMLInputElement | HTMLTextAreaElement} field */
  #markInvalid(field) {
    field.setAttribute('aria-invalid', 'true');

    const message = document.createElement('p');
    message.className = ERROR;
    message.textContent = this.#message(field);

    // `.angebot-formular__field` is a column, so the message goes inside it, under the input.
    // Appending it *after* would make it a sibling flex item — and for the name/postcode rows,
    // which are flex rows, that would park the message beside the field instead of below it.
    const field_wrapper = field.closest('.angebot-formular__field');
    if (field_wrapper) {
      field_wrapper.append(message);
      return;
    }

    // A `.checkbox` is a row (box + label), so its message belongs after the whole thing.
    const checkbox = field.closest('.checkbox');
    checkbox?.insertAdjacentElement('afterend', message);
  }

  /** @param {HTMLInputElement | HTMLTextAreaElement} field */
  #clearFieldError(field) {
    field.removeAttribute('aria-invalid');

    const field_wrapper = field.closest('.angebot-formular__field');
    if (field_wrapper) {
      for (const node of field_wrapper.querySelectorAll(`.${ERROR}`)) node.remove();
      return;
    }

    const message = field.closest('.checkbox')?.nextElementSibling;
    if (message?.classList.contains(ERROR)) message.remove();
  }

  #clearErrors() {
    for (const node of this.querySelectorAll(`.${ERROR}`)) node.remove();
    for (const node of this.querySelectorAll('[aria-invalid]')) node.removeAttribute('aria-invalid');
  }

  /** Ticks the accessories listed in `?zubehoer=`. */
  #applyUrlSelection() {
    const raw = new URL(window.location.href).searchParams.get('zubehoer');
    if (!raw) return;

    // Split on ':' as well, so a future "handle:variantId" format degrades instead of breaking.
    const wanted = new Set(
      raw
        .split(',')
        .map((entry) => (entry.split(':')[0] ?? '').trim())
        .filter(Boolean)
    );

    if (wanted.size === 0) return;

    for (const input of this.#toggles()) {
      if (wanted.has(input.dataset.zubehoerHandle ?? '')) input.checked = true;
    }
  }

  #syncSelects() {
    for (const input of this.#toggles()) this.#syncSelect(input);
  }

  /** @param {HTMLInputElement} toggle */
  #syncSelect(toggle) {
    const select = toggle.closest(ROW)?.querySelector('[data-zubehoer-select]');
    if (select instanceof HTMLSelectElement) select.disabled = !toggle.checked;
  }

  /** @returns {HTMLInputElement[]} */
  #toggles() {
    return [...this.querySelectorAll('input[data-zubehoer-handle]')].filter(
      (node) => node instanceof HTMLInputElement
    );
  }
}

if (!customElements.get('angebot-formular-component')) {
  customElements.define('angebot-formular-component', AngebotFormular);
}
